import React from 'react';

import '~/assets/styles/global.scss';
import '~/assets/styles/grid.css';
import { AuthProvider, QueryProvider } from '~/app/providers';
import { getLocaleFromPath, persistLanguage, resolveLanguage } from '~/shared/language-storage';
import scrollTo from 'gatsby-plugin-smoothscroll';

import intlConfig from './intl.config';

const { defaultLanguage } = intlConfig;

export const wrapRootElement = ({ element }) => (
  <React.StrictMode>
    <QueryProvider>
      <AuthProvider>{element}</AuthProvider>
    </QueryProvider>
  </React.StrictMode>
);

export const shouldUpdateScroll = ({ routerProps, ...props }) => {
  const { location } = routerProps;
  const { hash, state } = location;
  if (!state) return true;
  if (hash) {
    setTimeout(() => scrollTo(hash, 'start'), 110);
    return false;
  }
  setTimeout(() => scrollTo('body', 'start'), 110);

  return false;
};

/**
 * Boot-time language handling (runs once per full page load):
 * - a locale in the URL is an explicit choice → persist it and stay;
 * - otherwise resolve the preferred language (shared cookie → localStorage →
 *   legacy keys → browser languages → default) and redirect away from the
 *   unprefixed (default-language) URL when the target differs from the default.
 */
export const onClientEntry = () => {
  window.dataLayer = window.dataLayer || [];

  const pathname = window.location.pathname;
  const currentLocale = getLocaleFromPath(pathname);

  if (currentLocale) {
    persistLanguage(currentLocale);
    return;
  }

  const targetLocale = resolveLanguage();
  persistLanguage(targetLocale);

  if (targetLocale !== defaultLanguage) {
    const search = window.location.search || '';
    const newPath = pathname === '/' ? `/${targetLocale}/` : `/${targetLocale}${pathname}`;
    window.location.replace(`${newPath}${search}`);
  }
};

/**
 * Keeps the persisted language in sync with the URL after client-side
 * navigations (e.g. when the user switches the language via the header dropdown).
 *
 * @param {object} props - Gatsby route update props
 * @param {object} props.location - Current location object
 */
export const onRouteUpdate = ({ location }) => {
  const locale = getLocaleFromPath(location?.pathname);
  if (locale) persistLanguage(locale);
};
